/* @flow */

import React from 'react';
import { connect } from 'react-redux';

import LoadingComponent from 'components/loading';
import TwoFactorConfirmDialogComponent from 'components/twoFactorConfirmDialog';
import ChangePasswordViewComponent from './view';

import ChangePasswordForm from './model';

import UserService from 'services/UserService';
import { handleApiFormResponse, handleFormValidation } from 'lib/forms';
import loadConstraints from 'lib/validation/loadConstraints';
import { validateField, validateForm } from 'lib/validation/validator';
import NotificationHelper from 'lib/notifications';
import {history} from 'store/index';

import { PROTECTED_APP } from 'constants/pageRoutes';
import { ROLE_SELLER } from 'constants/companyRoles';

type ChangePasswordComponentProps = {
}

type ChangePasswordComponentState = {
  constraints: any;
  errors: Map<string,?string>;
  form: ChangePasswordForm;
  formErrors: string[];
  isLoading: boolean;
  isProcessing: boolean;
  twoFactorState: any;
  twoFactorMode: string;
  resetDialogOpen: boolean;
  isProcessingReset: boolean;
  twoFactorDialogOpen: boolean;
}

class ChangePasswordComponent extends React.Component<ChangePasswordComponentProps, ChangePasswordComponentState> {

  constructor(props) {
    super(props);

    this.state = {
      constraints: null,
      errors: null,
      form: new ChangePasswordForm(),
      formErrors: [],
      isLoading: true,
      isProcessing: false,
      twoFactorState: null,
      twoFactorMode: 'view',
      resetDialogOpen: false,
      isProcessingReset: false,
      twoFactorDialogOpen: false
    };
  }

  componentDidMount = async () => {
    try {
      const constraints = await loadConstraints('Password', ['currentPassword', 'password', 'confirmPassword']);
      const twoFactorState = await UserService.getTwoFactorState();
      this.setState({
        constraints,
        errors: new Map(Object.keys(constraints).map(e => [e, undefined])),
        isLoading: false,
        twoFactorState
      });
    } catch (e) {
      console.error(e);
    }
  };

  handleCurrentPasswordChange = (value: string) => {
    const validationResult = validateField(value, this.state.constraints.currentPassword);
    const errors = this.state.errors;
    errors.set('currentPassword', validationResult ? validationResult[0] : null);

    const form = this.state.form;
    form.currentPassword = value;
    this.setState({ form, errors });
  };

  handlePasswordChange = (value: string) => {
    const validationResult = validateField(value, this.state.constraints.password);
    const errors = this.state.errors;
    errors.set('password', validationResult ? validationResult[0] : null);

    if (this.state.form.confirmPassword.length > 2) {
      const validationResults = validateForm({ currentPassword: this.state.form.currentPassword, password: value, confirmPassword: this.state.form.confirmPassword }, this.state.constraints);
      let validationResult2 = validationResults && validationResults.confirmPassword ? validationResults.confirmPassword[0] : null;
      if (validationResult2 !== null && validationResult2.includes(' ')) {
        const split = validationResult2.split(' ');
        validationResult2 = split.pop();
      }
      errors.set('confirmPassword', validationResult2);
    }

    const form = this.state.form;
    form.password = value;
    this.setState({ form, errors });
  };

  handleConfirmPasswordChange = (value: string) => {
    const validationResults = validateForm({ currentPassword: this.state.form.currentPassword, password: this.state.form.password, confirmPassword: value }, this.state.constraints);
    let validationResult = validationResults && validationResults.confirmPassword ? validationResults.confirmPassword[0] : null;
    if (validationResult !== null && validationResult.includes(' ')) {
      const split = validationResult.split(' ');
      validationResult = split.pop();
    }

    const errors = this.state.errors;
    errors.set('confirmPassword', validationResult);

    const form = this.state.form;
    form.confirmPassword = value;
    this.setState({ form, errors });
  };

  validateForm = () => handleFormValidation.call(this);

  submitForm = async () => {
    if (!this.validateForm()) return;

    const ts = this.state.twoFactorState || {};
    const needs2fa = !!(ts.qrActivated || ts.smsActivated);
    if (needs2fa) {
      this.setState({ twoFactorDialogOpen: true, formErrors: [] });
      return;
    }

    this.setState({ formErrors: [], isProcessing: true });
    try {
      await this.doChangePassword();
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  };

  doChangePassword = async ({ twoFactorPin, smsCode } = {}) => {
    const request = this.state.form.toRequest(twoFactorPin, smsCode);
    await UserService.changePassword(request);
    NotificationHelper.createNotification(NotificationHelper.TYPE_SUCCESS, 'notifications.password.succes');
    history.push(`${PROTECTED_APP}`);
  };

  startTwoFactorSetup = () => this.setState({ twoFactorMode: 'setup' });

  cancelTwoFactorSetup = () => this.setState({ twoFactorMode: 'view' });

  completeTwoFactorSetup = async () => {
    try {
      await UserService.notifyTwoFactorUpdated();
    } catch (e) {
      console.error(e);
    }

    try {
      const twoFactorState = await UserService.getTwoFactorState();
      this.setState({ twoFactorState, twoFactorMode: 'view' });
      NotificationHelper.createNotification(NotificationHelper.TYPE_SUCCESS, 'notifications.2fa.success');
    } catch (e) {
      console.error(e);
      this.setState({ twoFactorMode: 'view' });
    }
  };

  openResetDialog = () => this.setState({ resetDialogOpen: true });

  closeResetDialog = () => this.setState({ resetDialogOpen: false });

  confirmReset = async () => {
    this.setState({ isProcessingReset: true });
    try {
      const isSeller = this.props.activeCompanyRole === ROLE_SELLER;
      await UserService.requestTwoFactorReset(this.props.activeCompanyId, isSeller);
      this.setState({ isProcessingReset: false, resetDialogOpen: false });
      NotificationHelper.createNotification(NotificationHelper.TYPE_SUCCESS, 'twofactor.reset.success');
    } catch (e) {
      // e.g. blocked while impersonating (Prod): surface the standard red error notification.
      console.error(e);
      this.setState({ isProcessingReset: false, resetDialogOpen: false });
      NotificationHelper.createNotification(NotificationHelper.TYPE_ERROR);
    }
  };

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    const ts = this.state.twoFactorState || {};
    const hasMethod = !!(ts.qrActivated || ts.smsActivated);
    let methodLabelId = '';
    if (ts.qrActivated && ts.smsActivated) methodLabelId = 'twofactor.method.both';
    else if (ts.smsActivated) methodLabelId = 'twofactor.method.sms';
    else if (ts.qrActivated) methodLabelId = 'twofactor.method.qr';

    return (
      <React.Fragment>
        <ChangePasswordViewComponent
          errors={this.state.errors}
          form={this.state.form}
          formErrors={this.state.formErrors}
          isProcessing={this.state.isProcessing}
          handleCurrentPasswordChange={this.handleCurrentPasswordChange}
          handlePasswordChange={this.handlePasswordChange}
          handleConfirmPasswordChange={this.handleConfirmPasswordChange}
          submitForm={this.submitForm}
          twoFactorMode={this.state.twoFactorMode}
          hasMethod={hasMethod}
          methodLabelId={methodLabelId}
          isImpersonating={this.props.isImpersonating}
          resetDialogOpen={this.state.resetDialogOpen}
          isProcessingReset={this.state.isProcessingReset}
          startTwoFactorSetup={this.startTwoFactorSetup}
          cancelTwoFactorSetup={this.cancelTwoFactorSetup}
          completeTwoFactorSetup={this.completeTwoFactorSetup}
          openResetDialog={this.openResetDialog}
          closeResetDialog={this.closeResetDialog}
          confirmReset={this.confirmReset}
        />
        <TwoFactorConfirmDialogComponent
          open={this.state.twoFactorDialogOpen}
          qrActivated={ts.qrActivated}
          smsActivated={ts.smsActivated}
          onSendSms={(qrpin) => UserService.sendPasswordChangeSmsCode(qrpin)}
          onConfirm={(codes) => this.doChangePassword(codes)}
          onCancel={() => this.setState({ twoFactorDialogOpen: false })}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  isImpersonating: state.auth.user.isImpersonating,
  activeCompanyId: state.auth.user.activeCompany.id,
  activeCompanyRole: state.auth.user.activeCompany.role
});

export default connect(mapStateToProps)(ChangePasswordComponent);
